import { useLazyQuery, useQuery } from "@apollo/client";
import classNames from "classnames";
import { get } from "lodash";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import ListContainer from "~/components/ListContainer";
import { ListContainerProps } from "~/components/ListContainer/ListContainer";
import { RelatedPosition } from "~/constants/enum";
import {
  GET_PRODUCTS_TRACKING,
  QUERY_RELATED_PRODUCT,
  QUERY_TRACK_VIEW_COLLECTION,
  QUERY_TRACK_VIEW_ITEM_RELATED,
  QUERY_TRACKING_IMPRESSION,
  QUERY_TRACKING_VIEW_RELATED,
} from "~/data/product/gql";
import { Product } from "~/services/product";
import { transformProductItems } from "~/utils/transform";
import ProductsSlider from "../ProductsSlider";
import { ProductsSliderProps } from "../ProductsSlider/ProductsSlider";
import useClient from "~/hooks/useClient";

export interface ProductsRelatedProps {
  title?: string;
  className?: string;
  sku?: any;
  ruleId: number;
  limit?: number;
  directLabel?: string;
  directUrl?: string;
  type?: "slider" | "grid";
  girdProps?: ListContainerProps;
  sliderProps?: ProductsSliderProps;
  position?: RelatedPosition;
  relatedTopProduct?: any;
  isMobile?: boolean;
}

const ProductsRelated: FC<ProductsRelatedProps> = ({
  title,
  className,
  sku,
  ruleId,
  limit,
  type = "slider",
  directLabel,
  directUrl,
  position,
  relatedTopProduct,
  isMobile,
}) => {
  const [startFetching, setStartFetching] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const isClient = useClient();

  const { data } = useQuery(QUERY_RELATED_PRODUCT, {
    variables: {
      sku,
      ruleId,
      limit: limit || 12,
    },
    skip: !sku || !ruleId || !isClient || !!relatedTopProduct,
    context: { fetchOptions: { method: "GET" } },
    onCompleted: () => {
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const [queryTrackViewItemRelated] = useLazyQuery(
    QUERY_TRACK_VIEW_ITEM_RELATED
  );

  const [queryTrackingViewList] = useLazyQuery(QUERY_TRACKING_VIEW_RELATED);
  const [queryTrackingViewCollection] = useLazyQuery(
    QUERY_TRACK_VIEW_COLLECTION
  );

  const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);

  const productList = useMemo(() => {
    return transformProductItems(
      get(relatedTopProduct ?? data, "relatedProductsByRule.items", [])
    );
  }, [relatedTopProduct, data]);

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus: productList.map((product) => product.sku + ""),
      },
      context: { fetchOptions: { method: "GET" } },
      skip: !productList.length,
    });

  const _list = useMemo(() => {
    if (loadingCollectionTracking || !dataCollectionTracking)
      return productList;
    return productList.map((product: Product) => {
      const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
        (item: any) => item.sku === product.sku
      );
      return {
        ...product,
        ...tracking,
      };
    });
  }, [loadingCollectionTracking, dataCollectionTracking, productList]);

  const handleOnEnter = () => {
    setIsIntersecting(true);
  };
  const handleStartFetching = (entries: IntersectionObserverEntry) => {
    setStartFetching(true);
  };
  const handleImpression = (products: any[]) => {
    console.log("handleImpression", ruleId, products);
    queryTrackViewItemRelated({
      variables: {
        block_id: ruleId,
        skus: products?.map((prod) => prod?.sku),
      },
    });
    relatedTrackingImpression({
      variables: {
        skus: products?.map((prod) => prod?.sku),
        block_id: ruleId,
        type: "related",
        search_query: "",
      },
    });
  };

  const initTracking = useRef(false);
  useEffect(() => {
    if (
      !!isIntersecting &&
      !!ruleId &&
      !!productList?.length &&
      !initTracking.current
    ) {
      console.log("queryTrackingViewList", ruleId, productList);
      queryTrackingViewList({
        variables: {
          block_id: ruleId,
        },
      });
      queryTrackingViewCollection({
        variables: {
          block_id: ruleId,
          type: "related",
        },
      });
      initTracking.current = true;
    }
  }, [isIntersecting, productList, ruleId]);

  const _title = title || get(data, "relatedProductsByRule.name", "");
  // từ bắt đầu, startWith = brand  => hide => all title

  if (!ruleId) return null;

  if (!_list?.length) {
    return null;
  }

  return (
    <div className={classNames("flex flex-col", className)}>
      {/* {type === 'grid' ? (
                <React.Fragment>
                    <ListContainer
                        showListLayout={false}
                        productItemClass="!opacity-100"
                        defaultLayout="grid-6"
                        listProductClass="!py-0 "
                        products={productList}
                        pageSize={12}
                        title={_title}
                        onEnter={handleOnEnter}
                        listKey={ruleId as any}
                    />
                </React.Fragment>
            ) : (
                
            )} */}
      <ProductsSlider
        listkey={ruleId as any}
        loop={false}
        title={_title}
        products={_list}
        // loading={isClient ? loading : true}
        onEnter={handleOnEnter}
        onBeforeIntersection={handleStartFetching}
        onImpressions={handleImpression}
        directLabel={directLabel}
        directUrl={directUrl}
        productItemProps={
          position === RelatedPosition.ProductContentTop
            ? {
                displayInfo: [],
              }
            : {}
        }
        isMobile={isMobile}
      />
    </div>
  );
};

export default ProductsRelated;
